/**
 * bulletin-records/render — 레코드 페이지의 그리기. 순수 함수(상태 → HTML 문자열).
 *
 * 머리(renderRecordsHead) · type 탭(renderTypeTabs) · 줄 표(renderRecords) · assemble_error 칸(renderAssembleError — ③ 에서 옮겨 옴).
 * CSS 접두 `br-` = bulletin-records. 탭 · status 배지 · 표는 ③ 의 `ce-*` 를 그대로 빌린다.
 */
import { renderBulletinStatus } from '../catalog-entry/render';
import type { AssembledRecord, BulletinDocView, EAssembledRecordType } from '../../api/client';
import { escapeHtml, fmtDateTime } from '../../lib/cells';
import { E_RECORD_TYPE_FILTERS } from './types';
import type { BulletinRecordsData, ERecordTypeFilter } from './types';

// ─── 라벨 ───────────────────────────────────────────────────────────
const TYPE_LABELS: Record<ERecordTypeFilter, string> = {
  all:               '전부',
  doc:               '문서',
  chassis:           '섀시',
  preconfig_chassis: '완제품',
  part:              '파트',
  rule:              '규칙',
  group_note:        '묶음 메모',
};

// ─── 머리 ───────────────────────────────────────────────────────────
/** 머리 — 번호 · 제목 · status · ③ 로 돌아가는 링크(data-link) */
export function renderRecordsHead(data: BulletinRecordsData): string {
  const doc = data.doc;
  if (doc === null) return '';

  return `
    <header class="ce-doc-head">
      <a class="btn-ghost" data-link href="/catalog-entry/bulletin">← 불레틴</a>
      <h2>${doc.vendor} ${doc.doc_no} <small>${escapeHtml(doc.generation ?? '')}</small></h2>
      ${renderBulletinStatus(doc.status)}
      <span class="text-muted">레코드 ${data.records.length}줄 · ${escapeHtml(fmtDateTime(doc.updated_at))}</span>
    </header>
    <div class="ce-doc-title">${escapeHtml(doc.title ?? '(제목 없음)')}</div>`;
}

// ─── type 탭 ────────────────────────────────────────────────────────
/** type 탭 — all + 여섯. 탭마다 그 type 의 줄 수. 지금 탭에 active */
export function renderTypeTabs(data: BulletinRecordsData): string {
  const tabs = E_RECORD_TYPE_FILTERS.map((f) =>
    `<button type="button" class="ce-tab${f === data.typeFilter ? ' active' : ''}" data-action="records-type" data-type="${f}">${TYPE_LABELS[f]} ${countOf(data.records, f)}</button>`);

  return `<div class="ce-tabs">${tabs.join('')}</div>`;
}

function countOf(records: AssembledRecord[], filter: ERecordTypeFilter): number {
  return filter === 'all' ? records.length : records.filter((r) => r.type === filter).length;
}

// ─── 줄 표 ──────────────────────────────────────────────────────────
/** 줄 표 — typeFilter 로 거른 레코드. type 마다 열이 달라 한 줄 요약 + 상세. 없으면 안내 */
export function renderRecords(data: BulletinRecordsData): string {
  const shown = data.typeFilter === 'all' ? data.records : data.records.filter((r) => r.type === data.typeFilter);
  if (shown.length === 0) return `<div class="text-muted br-note">이 type 의 레코드가 없습니다.</div>`;

  const rows = shown.map((record, index) => `
    <tr>
      <td class="num">${index + 1}</td>
      <td><span class="br-type br-type-${record.type}">${TYPE_LABELS[record.type]}</span></td>
      <td class="num">${positionOf(record)}</td>
      <td>${summaryOf(record)}</td>
      <td class="text-muted">${detailOf(record)}</td>
    </tr>`);

  return `
    <div class="deal-table-wrap">
      <table class="deal-items-table ce-table br-table">
        <thead><tr><th>#</th><th>type</th><th>표.줄</th><th>요약</th><th>상세</th></tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>`;
}

/** 원문의 자리 — 표.줄. doc 레코드는 자리가 없다 */
function positionOf(record: AssembledRecord): string {
  return record.type === 'doc' ? '-' : `${record.table}.${record.row}`;
}

/** type 마다 그 줄이 무엇인지 한 줄로 */
function summaryOf(record: AssembledRecord): string {
  switch (record.type) {
    case 'doc':               return `<b>${escapeHtml(record.title)}</b> <span class="text-muted">${escapeHtml(record.model)}</span>`;
    case 'chassis':           return `<code>${escapeHtml(record.sku)}</code> ${escapeHtml(record.name)}`;
    case 'preconfig_chassis': return `<code>${escapeHtml(record.sku)}</code> ${escapeHtml(record.name)} <span class="text-muted">${record.config_type}</span>`;
    case 'part':              return `<code>${escapeHtml(record.pn)}</code> ${escapeHtml(record.desc)}`;
    case 'rule':              return escapeHtml(record.text);
    case 'group_note':        return escapeHtml(record.text);
  }
}

/** type 마다 붙은 것의 개수 — specs · 격자 · notes · 경로 */
function detailOf(record: AssembledRecord): string {
  switch (record.type) {
    case 'doc':               return `specs ${record.specs.length} · notes ${record.notes.length} · 판 ${record.version ?? '-'} ${escapeHtml(record.published_date ?? '')}`;
    case 'chassis':           return `attrs ${Object.keys(record.attrs).length} · notes ${record.notes.length}`;
    case 'preconfig_chassis': return `attrs ${Object.keys(record.attrs).length} · notes ${record.notes.length}${record.variants.length > 0 ? ` · 변종 ${record.variants.length}` : ''}`;
    case 'part':              return `specs ${Object.keys(record.specs).length}${gridLabel(record.grid === null ? 0 : Object.keys(record.grid.by_axis).length)} · notes ${record.notes.length} · ${escapeHtml(record.heading_path.join(' › '))}`;
    case 'rule':              return escapeHtml(record.heading_path.join(' › '));
    case 'group_note':        return escapeHtml(record.heading_path.join(' › '));
  }
}

/** 격자가 있으면 " · 격자 N항목", 없으면 '' */
function gridLabel(itemCount: number): string {
  return itemCount === 0 ? '' : ` · 격자 ${itemCount}항목`;
}

// ─── assemble_error 칸 ──────────────────────────────────────────────
/** assemble_error 칸 — 사람이 적는 버그 칸. textarea + 저장 · 비우기(③ 의 그 칸 그대로) */
export function renderAssembleError(doc: BulletinDocView): string {
  return `
    <section class="ce-assemble-error br-assemble-error">
      <label>assemble_error — 사람이 적는 버그 칸</label>
      <textarea class="br-why" rows="4" data-field="why">${escapeHtml(doc.assemble_error ?? '')}</textarea>
      <div class="ce-actions">
        <button type="button" class="btn-primary" data-action="bulletin-error-save">저장</button>
        <button type="button" class="btn-ghost" data-action="bulletin-error-clear" ${doc.assemble_error === null ? 'disabled' : ''}>비우기</button>
      </div>
    </section>`;
}

// EAssembledRecordType 는 TYPE_LABELS 의 키가 여섯 type 을 다 덮는지 tsc 가 보게 하는 데만 쓴다
export type { EAssembledRecordType };
